'use client';

import { useState, useCallback, useMemo, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, setMonth, setYear } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FieldTrigger } from '@/components/ui/field-trigger';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarIcon } from '@icons/CalendarIcon';
import {
  CalendarPickerDropdown,
  calendarClassNames,
  MONTHS,
  FORMATTERS,
} from '@/components/ui/calendar-date-picker';
import { cn } from '@/lib/utils';

export interface DateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  onStartDateChange?: (date: Date | undefined) => void;
  onEndDateChange?: (date: Date | undefined) => void;
  noEndDate?: boolean;
  onNoEndDateChange?: (checked: boolean) => void;
  placeholder?: string;
  endPlaceholder?: string;
  noEndDateLabel?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  fromYear?: number;
  toYear?: number;
  className?: string;
}

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  noEndDate = false,
  onNoEndDateChange,
  placeholder = 'Start date',
  endPlaceholder = 'End date',
  noEndDateLabel = 'Indefinite ∞',
  disabled = false,
  minDate,
  maxDate,
  fromYear = 2020,
  toYear = 2030,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<'start' | 'end'>('start');

  // Calendar display month tracks the currently-edited date
  const activeDate = editing === 'start' ? startDate : endDate;
  const [month, setDisplayMonth] = useState<Date>(activeDate || new Date());

  useEffect(() => {
    if (activeDate) {
      setDisplayMonth(activeDate);
    }
  }, [activeDate]);

  const handleTriggerClick = useCallback(
    (target: 'start' | 'end', e: React.MouseEvent) => {
      e.stopPropagation();
      setEditing(target);
      const date = target === 'start' ? startDate : endDate;
      if (date) setDisplayMonth(date);
      setOpen(true);
    },
    [startDate, endDate],
  );

  const handleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) return;

      if (editing === 'start') {
        onStartDateChange?.(date);
      } else {
        onEndDateChange?.(date);
        // Picking an end date unchecks "No end date"
        if (noEndDate) onNoEndDateChange?.(false);
      }
      setOpen(false);
    },
    [editing, noEndDate, onStartDateChange, onEndDateChange, onNoEndDateChange],
  );

  const handleNoEndDateChange = useCallback(
    (checked: boolean) => {
      onNoEndDateChange?.(checked);
      if (checked) {
        onEndDateChange?.(undefined);
        // In start popover: close only if start date already selected
        // In end popover: always close
        if (editing === 'start') {
          if (startDate) setOpen(false);
        } else {
          setOpen(false);
        }
      } else {
        // Unchecking: close in start popover, stay open in end popover
        if (editing === 'start') setOpen(false);
      }
    },
    [onNoEndDateChange, onEndDateChange, editing, startDate],
  );

  const handleMonthChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setMonth(prev, parseInt(val)));
  }, []);

  const handleYearChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setYear(prev, parseInt(val)));
  }, []);

  // Disabled day matchers for the calendar
  const disabledMatchers = useMemo(() => {
    // When Indefinite is checked and the user opens the end-date popover, no
    // dates are selectable until they uncheck "Indefinite ∞" — react-day-picker
    // accepts `true` as a Matcher that disables every day.
    if (noEndDate && editing === 'end') return true;
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    // When editing end date, disallow dates before start date
    const effectiveMin = editing === 'end' && startDate ? startDate : minDate;
    if (effectiveMin) matchers.push({ before: effectiveMin });
    if (maxDate) matchers.push({ after: maxDate });
    return matchers.length > 0 ? matchers : undefined;
  }, [noEndDate, editing, startDate, minDate, maxDate]);

  const monthItems = useMemo(
    () => MONTHS.map((name, i) => ({ label: name, value: String(i) })),
    [],
  );

  const yearItems = useMemo(
    () =>
      Array.from({ length: toYear - fromYear + 1 }, (_, i) => ({
        label: String(fromYear + i),
        value: String(fromYear + i),
      })),
    [fromYear, toYear],
  );

  // Format display values
  const startDisplay = startDate ? format(startDate, 'dd MMM yyyy') : null;
  const endDisplay = noEndDate ? noEndDateLabel : endDate ? format(endDate, 'dd MMM yyyy') : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn('flex items-center gap-3 w-full', className)}>
          <FieldTrigger
            disabled={disabled}
            onClick={(e) => handleTriggerClick('start', e)}
            className={cn('flex-1 min-w-0 text-[15px] hover:border-dinnerplate-white', !startDisplay && 'text-grey-light')}
            aria-label="Start date"
          >
            <span className="truncate">{startDisplay || placeholder}</span>
            <CalendarIcon className="size-5 shrink-0 text-off-black" />
          </FieldTrigger>

          <span className="text-[15px] font-inter text-grey select-none" aria-hidden="true">&mdash;</span>

          <FieldTrigger
            disabled={disabled}
            onClick={(e) => handleTriggerClick('end', e)}
            className={cn('flex-1 min-w-0 text-[15px] hover:border-dinnerplate-white', !endDisplay && 'text-grey-light')}
            aria-label="End date"
          >
            <span className="truncate">{endDisplay || endPlaceholder}</span>
            <CalendarIcon className="size-5 shrink-0 text-off-black" />
          </FieldTrigger>
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] border-0 p-0 bg-transparent shadow-none animate-dropdown-in motion-reduce:animate-none"
        align="start"
        sideOffset={6}
      >
        <div className="w-full border-[0.5px] border-dinnerplate-white rounded-input bg-off-white font-inter shadow-soft">
          <div className="flex flex-col gap-6 p-6">
            <div className="flex gap-3">
              <CalendarPickerDropdown
                value={String(month.getMonth())}
                items={monthItems}
                onSelect={handleMonthChange}
                className="flex-1 h-[45px]"
              />
              <CalendarPickerDropdown
                value={String(month.getFullYear())}
                items={yearItems}
                onSelect={handleYearChange}
                className="flex-1 h-[45px]"
              />
            </div>

            <DayPicker
              mode="single"
              selected={activeDate}
              onSelect={handleDateSelect}
              month={month}
              onMonthChange={setDisplayMonth}
              showOutsideDays
              hideNavigation
              weekStartsOn={0}
              disabled={disabledMatchers}
              formatters={FORMATTERS}
              // Override the shared 18px row spacing — DateRangePicker's
              // larger 433px frame uses 30px to match Figma node 14434:186846
              // (`flex flex-col gap-[30px]` on the Date container). Spread
              // first so any future calendarClassNames additions still apply
              // unless we override.
              classNames={{
                ...calendarClassNames,
                month_grid: 'w-full border-separate border-spacing-x-0 border-spacing-y-[30px] -my-[30px]',
              }}
            />
          </div>

          <div className="border-t border-dinnerplate-white" />

          <label className="flex items-center gap-4 px-8 py-6 cursor-pointer">
            <Checkbox
              checked={noEndDate}
              onCheckedChange={(checked) => handleNoEndDateChange(checked === true)}
              aria-label={noEndDateLabel}
            />
            <span
              className={cn(
                'text-[15px] font-inter select-none transition-colors',
                noEndDate ? 'text-off-black' : 'text-grey',
              )}
            >
              {noEndDateLabel}
            </span>
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
